import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

import MobileSectionHeader from 'components/Layout/MobileShell/MobileSectionHeader';
import MoreSheet from 'components/Layout/MobileShell/MoreSheet';
import Button from 'components/DesignSystem/Button';
import Icon from 'components/DesignSystem/Icon';
import RichHtml from 'components/RichHtml';
import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import useBulletinDownload from 'utils/hooks/videos/useBulletinDownload';
import {
  VideoData,
  VideoProduct,
  videoProductToPageTitle,
  videoProductToRoute,
} from 'helper/videos';
import { cssVar } from 'constants/theme';

import FormSheet from '../FormSheet';
import DeleteDialog from '../DeleteDialog';
import Toast from '../Toast';

import './index.scss';

export default function Player({
  videoData,
  product,
  viewOnly,
}: {
  videoData: VideoData;
  product: VideoProduct;
  viewOnly: boolean;
}) {
  const navigate = useNavigate();
  const userIsAdmin = useSelector(selectUserIsAdmin);
  const isAdminView = userIsAdmin && !viewOnly;
  const productRoute = videoProductToRoute[product];
  const categoryRoute = `${productRoute}/categories/${videoData.categoria}`;

  const { onDownload, downloadLoading } = useBulletinDownload(videoData.id, product);
  const videoHasAFile = typeof videoData.file_name === 'string';

  const [moreOpen, setMoreOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toast, setToast] = useState('');

  const date = new Date(videoData.uri.release_time).toLocaleDateString('pt-BR');

  return (
    <div className="videos-mobile-player">
      <MobileSectionHeader
        backLabel={videoProductToPageTitle[product]}
        title={videoData.video_title}
        onBack={() => navigate(categoryRoute)}
        onAvatarClick={() => setMoreOpen(true)}
      />

      <div className="videos-mobile-player-body">
        <div className="videos-mobile-player-frame">
          <iframe
            title={videoData.video_title}
            src={videoData.uri.player_embed_url}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </div>

        <span className="videos-mobile-card-date">{date}</span>
        <h1 className="videos-mobile-player-title">{videoData.video_title}</h1>

        {videoHasAFile && (
          <Button
            icon="download"
            className="videos-mobile-player-download"
            disabled={downloadLoading}
            onClick={onDownload}
          >
            {downloadLoading ? <Spinner size="sm" /> : 'Baixar apresentação'}
          </Button>
        )}

        {isAdminView && (
          <div className="videos-mobile-player-actions">
            <Button
              type="secondary"
              icon="edit"
              className="flex-fill"
              onClick={() => setEditOpen(true)}
            >
              Editar
            </Button>
            <button
              type="button"
              aria-label="Excluir vídeo"
              className="videos-mobile-card-action filled"
              onClick={() => setDeleteOpen(true)}
            >
              <Icon iconType="trash" color={cssVar('text-on-accent')} size="small" />
            </button>
          </div>
        )}

        <RichHtml html={videoData.video_description} className="videos-mobile-player-desc" />
      </div>

      {isAdminView && (
        <FormSheet
          open={editOpen}
          editingId={videoData.id}
          product={product}
          dismiss={() => setEditOpen(false)}
          onSaved={(message) => {
            setEditOpen(false);
            setToast(message);
          }}
        />
      )}
      <DeleteDialog
        video={deleteOpen ? { id: videoData.id, titulo: videoData.video_title } : null}
        product={product}
        dismiss={() => setDeleteOpen(false)}
        onDeleted={() => navigate(categoryRoute)}
      />
      <Toast message={toast} onClose={() => setToast('')} />
      <MoreSheet open={moreOpen} dismiss={() => setMoreOpen(false)} />
    </div>
  );
}
